import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import Header from "../../../components/header/Header";
import Sidebar from "../../../components/sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import PaystackPop from "@paystack/inline-js";
import axios from "axios";
import api from "../../../utils/api";
import "../../account/SubscriptionSection.css";
import SubscriptionSkeleton from "../../../components/skeletons/SubscriptionSkeleton";

const SubscriptionSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user?.role || "tutor"); // Student, Tutor, Institution
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null); // Will come from auth/context later
  const [isProcessing, setIsProcessing] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subLoading, setSubLoading] = useState(true);

  useEffect(() => {
    const cached = localStorage.getItem("plans");

    if (cached) {
      setPlans(JSON.parse(cached));
    }

    const fetchPlans = async () => {
      const res = await api.get("/subscription/plans");

      if (res.data.success) {
        setPlans(res.data.plans);
        localStorage.setItem("plans", JSON.stringify(res.data.plans));
      }
    };

    fetchPlans();
  }, []);


  console.log("User:", user);

  const userEmail = user?.email;

  const PLAN_UI_CONFIG = {
    Basic: {
      description: "Perfect for getting started",
      color: "#f8fafc",
      features: [
        "Access to basic features",
        "Limited courses",
        "Email support",
        "Basic analytics",
      ],
      popular: false,
    },
    Premium: {
      description: "Full access experience",
      color: "#fef3c7",
      features: [
        "Everything in Pro",
        "Institution features",
        "Custom branding",
        "API access",
        "Dedicated manager",
      ],
      popular: false,
    },
  };

  const groupedPlans = [];

  plans.forEach((plan) => {
    const baseName = plan.name.replace(" Monthly", "").replace(" Yearly", "");

    let existing = groupedPlans.find((p) => p.name === baseName);

    if (!existing) {
      existing = {
        name: baseName,
        monthly: null,
        yearly: null,
      };
      groupedPlans.push(existing);
    }

    if (plan.interval === "monthly") {
      existing.monthly = plan;
    } else {
      existing.yearly = plan;
    }
  });

  const currentPlans = groupedPlans.map((p) => {
    const ui = PLAN_UI_CONFIG[p.name] || {};

    return {
      name: p.name,
      displayPrice:
        billing === "monthly" ? p.monthly?.amount : p.yearly?.amount,
      activePlanCode:
        billing === "monthly" ? p.monthly?.planCode : p.yearly?.planCode,

      // ✅ Merge UI config
      description: ui.description || "",
      features: ui.features || [],
      color: ui.color || "#fff",
      popular: p.monthly?.amount > 10000,
    };
  });

  // Fetch current subscription (optional - for showing Manage button)
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await api.get("/subscription/current");
        if (res.data.success && res.data.subscription) {
          setUserSubscription(res.data.subscription);
        }
      } catch {
        setUserSubscription(null);
      } finally {
        setSubLoading(false);
      }
    };
    if (user) fetchSubscription();
    else setSubLoading(false);
  }, [user]);

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error("Please select a plan");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    const planCodeToUse = selectedPlan.activePlanCode;

    if (!planCodeToUse) {
      toast.error("Plan code is missing. Please contact support.");
      return;
    }

    setIsProcessing(true);

    try {
      const response = await api.post(`/subscription/initialize`, {
        email: userEmail,
        amount: selectedPlan.displayPrice,
        planName: selectedPlan.name,
        planCode: planCodeToUse, // Must be passed from Paystack plan
        billingCycle: billing,
        userId: user?._id,
        role: selectedRole,
      });

      console.log("Subscription initialization response:", response.data);

      if (response.data.success) {
        // Redirect to Paystack checkout page
        window.location.href = response.data.authorization_url;
      } else {
        // alert("Failed to initialize payment. Please try again.");
        toast.error("Failed to initialize payment. Please try again.");
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Subscription initialization error:", error);
      // alert("Something went wrong. Please try again later.");
      toast.error("Something went wrong. Please try again later.");
      setIsProcessing(false);
    }
  };

  // ← Helper: is this plan the user's current active plan?
  const isCurrentPlan = (planName) => {
    if (!userSubscription || userSubscription.status !== "active") return false;
    return userSubscription.plan?.toLowerCase() === planName?.toLowerCase();
  };

  const getPlanButtonLabel = (plan) => {
    if (isCurrentPlan(plan.name)) return "Current Plan";
    if (hasActiveSubscription) return `Switch to ${plan.name}`;
    return plan.name === "Basic" ? "Subscribe" : `Upgrade to ${plan.name}`;
  };

  const hasActiveSubscription =
    userSubscription && userSubscription.status === "active";

  // Navigate to Manage Subscription Page (you can change this to your actual route)
  const goToManageSubscription = () => {
    navigate("/subscription-management");
  };

  const openModal = (plan) => {
    if (isCurrentPlan(plan.name)) return; // don't open modal for current plan
    setSelectedPlan(plan);
  };

  const closeModal = () => setSelectedPlan(null);


if (subLoading) return <SubscriptionSkeleton />;

  return (
    <div className="dashboard-layout">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <div className="dashboard-content subscription-page">
        <h1 className="subscription-page-title">Subscription Plan</h1>
        <p className="sub-page-subtitle">
          Choose the plan that works best for you
        </p>

        {/* Manage Subscription Button - Shows only if user has active plan */}
        {/* ← Active subscription banner */}
        {hasActiveSubscription && (
          <div className="active-sub-banner">
            <span>
              ✅ You're on the <strong>{userSubscription.plan}</strong> plan
              {userSubscription.nextBillingDate && (
                <>
                  {" "}
                  · renews{" "}
                  {new Date(
                    userSubscription.nextBillingDate,
                  ).toLocaleDateString("en-GB")}
                </>
              )}
            </span>
            <button
              className="manage-subscription-btn"
              onClick={() => navigate("/subscription-management")}
            >
              Manage Subscription
            </button>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="billing-toggle">
          <button
            className={billing === "monthly" ? "active" : ""}
            onClick={() => setBilling("monthly")}
          >
            Monthly
          </button>

          <button
            className={billing === "yearly" ? "active" : ""}
            onClick={() => setBilling("yearly")}
          >
            Annually <span className="discount">Save 20%</span>
          </button>
        </div>
        {/* Plans Grid */}

        <div className="plans-grid">
          {currentPlans.map((plan, index) => {
            const isCurrent = isCurrentPlan(plan.name);
            return (
              <div
                key={index}
                className={`plan-card ${plan.popular ? "popular" : ""} ${isCurrent ? "current-plan" : ""}`}
              >
                {plan.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                {isCurrent && (
                  <div className="current-plan-badge">Your Plan</div>
                )}{" "}
                {/* ← */}
                <div
                  className="plan-header"
                  style={{ backgroundColor: plan.color }}
                >
                  <div className="plan-badge">{plan.name}</div>
                  <div className="sub-price-container">
                    <span className="sub-price">
                      ₦{plan.displayPrice?.toLocaleString() || 0}
                    </span>
                    <span className="sub-period">
                      /{billing === "monthly" ? "month" : "year"}
                    </span>
                  </div>
                  <p className="sub-plan-desc">{plan.description}</p>
                </div>
                <div className="plan-body">
                  <button
                    className={`subscribe-btn ${isCurrent ? "current-plan-btn" : ""}`}
                    onClick={() => openModal(plan)}
                    disabled={isCurrent} // ← disabled if already on this plan
                  >
                    {getPlanButtonLabel(plan)}
                  </button>

                  <ul className="features-list">
                    {plan.features?.map((feature, i) => (
                      <li key={i}>✓ {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Subscription Modal */}={" "}
      {selectedPlan && (
        <div
          className="sub-modal-overlay"
          onClick={!isProcessing ? closeModal : undefined}
        >
          <div
            className="sub-modal-content"
            onClick={(e) => {
              if (!isProcessing) e.stopPropagation();
            }}
          >
            <div className="modal-header">
              <h3>{selectedPlan.name} Subscription Plan</h3>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <p className="sub-modal-subtitle">{selectedPlan.description}</p>
            <div className="sub-modal-price-box">
              <span className="sub-modal-price">
                ₦{selectedPlan.displayPrice?.toLocaleString()}
              </span>
              <span className="sub-modal-period">
                /{billing === "monthly" ? "month" : "year"}
              </span>
            </div>
            <p className="sub-modal-charge-text">
              You'll be charged{" "}
              <strong>₦{selectedPlan.displayPrice?.toLocaleString()}</strong>{" "}
              {billing === "monthly" ? "every month" : "every year"}
            </p>
            {hasActiveSubscription && (
              <p className="sub-modal-switch-note">
                ⚠️ This will replace your current{" "}
                <strong>{userSubscription.plan}</strong> plan.
              </p>
            )}
            <div className="sub-modal-features">
              {selectedPlan.features.map((feature, i) => (
                <div key={i} className="sub-modal-feature-item">
                  <span className="subscription-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <button
              className="sub-modal-subscribe-btn"
              onClick={handleSubscribe}
              disabled={isProcessing}
            >
              {isProcessing
                ? "Processing..."
                : hasActiveSubscription
                  ? `Switch to ${selectedPlan.name}`
                  : "Subscribe"}
            </button>
            <div className="sub-modal-secure-payment">🔒 Secure Payment</div>
            <p className="sub-modal-footer-text">
              You can cancel anytime • No hidden fees
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
export default SubscriptionSection;
